import { SegmentEffects } from '@opengov/app-core/analytics';
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_TITLE_LENGTH,
  MIN_TITLE_LENGTH,
  UNIQUENESS_VIOLATION
} from '@opengov/stories-shared/constants';
import { IPublication } from '@opengov/stories-shared/types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useEffect, useMemo, useState } from 'react';

import { publicationList as publicationListLocale } from '../../../../locale/stories.en.locale.json';
import { IPublicationModalMessages, ISessionUser, IStoriesFolder } from '../../../../types';
import { useCreatePublication } from '../PublicationList/hooks/usePublicationMutations';
import { navigateToPublicationView } from '../../../state';

import { styles } from './CreatePublicationModal.styles';

export type ISelectOption = {
  id: string;
  displayName: string;
};

export interface ICreatePublicationModalProps {
  isAdmin?: boolean;
  messages: IPublicationModalMessages;
  folderStructure?: IStoriesFolder[];
  user?: ISessionUser;
  entityName?: string;
  entityId: number;
  entitySubdomain: string;
  publicationList: IPublication[];
  onClose(): void;
}

export const CreatePublicationModal: React.FC<ICreatePublicationModalProps> = (props) => {
  const { isAdmin, entityId, entitySubdomain, messages, onClose, folderStructure, publicationList } =
    props;
  const [publicationTitle, setPublicationTitle] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [publicationDescription, setPublicationDescription] = useState('');
  const [folderId, setFolderId] = useState('');
  const [hasSameName, setHasSameName] = useState(false);
  const [isloading, setIsloading] = useState(false);
  const trimmedPublicationTitle = useMemo(() => publicationTitle.trim(), [publicationTitle]);
  const trimmedPublicationDescription = useMemo(() => publicationDescription.trim(), [publicationDescription]);

  const createMutation = useCreatePublication({
    title: publicationListLocale.somethingWentWrongError,
    body: publicationListLocale.failedToCreatePublication
  });

  let errorMessage: string | undefined;
  useEffect(() => {
    if (publicationList) {
      const hasMatch = !!publicationList.find(
        (publication) => publication.title.toLowerCase() === trimmedPublicationTitle.toLowerCase()
      );
      setHasSameName(hasMatch);
    }
  }, [trimmedPublicationTitle, publicationList]);

  if (hasSameName) {
    errorMessage = messages.noSameName;
  }

  const handleConfirmCreatePublication = () => {
    setShowDialog(false);
    // Admin guard moved from the retired effect — the modal should not be
    // openable for non-admins, but stay defensive.
    if (!isAdmin) return;
    setIsloading(true);
    createMutation.mutate(
      {
        title: trimmedPublicationTitle,
        description: trimmedPublicationDescription,
        entityId,
        entitySubdomain,
        folderId
      },
      {
        onSuccess: (publication: IPublication) => {
          void SegmentEffects.track({
            eventName: 'Stories: Publications Created',
            props: {}
          });
          onClose();
          navigateToPublicationView(entitySubdomain, publication.id);
        },
        onError: (error: Error) => {
          // Only a duplicate-title response surfaces here as inline validation.
          // Every other failure is already reported by the hook's error toast,
          // so flagging the name would be misleading.
          if (error?.message?.includes(UNIQUENESS_VIOLATION)) {
            setHasSameName(true);
          }
        },
        onSettled: () => {
          setIsloading(false);
        }
      }
    );
  };

  const onChangeSelectAction = (value: IStoriesFolder) => {
    value?.id && value?.displayName ? setFolderId(value.id) : setFolderId('');
  };

  const renderFolderDropdown = () => {
    const selectedFolder = folderStructure?.find((f) => f.id === folderId) ?? null;
    return (
      <Box sx={styles.folderDropdownContainer}>
        <Box sx={styles.listItem}>
          <Autocomplete
            options={folderStructure ?? []}
            getOptionLabel={(option) => option.displayName}
            value={selectedFolder}
            onChange={(_event, value) => onChangeSelectAction(value)}
            size="small"
            disablePortal
            renderOption={(props, option) => <li {...props}>{option.displayName}</li>}
            renderInput={(params) => (
              <TextField
                {...params}
                name="folder-id"
                placeholder={messages.moveStoriesPlaceholder}
                inputProps={{ ...params.inputProps, className: 'no-format' }}
              />
            )}
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="create-publication-dialog-title">
        <DialogTitle id="create-publication-dialog-title" sx={styles.dialogTitle}>
          {messages.createNewPublication}
          <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <Box data-test="create-publication-form">
            <Box>
              <Box sx={styles.labelRow}>
                <Typography id="create-publication-name-label" variant="body2" sx={styles.formLabel}>
                  {messages.publicationName}
                </Typography>
                <Typography variant="caption" sx={styles.characterCounter}>
                  {publicationTitle.length}/{MAX_TITLE_LENGTH}
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder={messages.enterNamePlaceholder}
                value={publicationTitle}
                onChange={(e) => setPublicationTitle(e.target.value)}
                inputProps={{ 'data-test': 'publication-title-input', maxLength: MAX_TITLE_LENGTH, className: 'no-format', 'aria-labelledby': 'create-publication-name-label' }}
              />
              {errorMessage && (
                <FormHelperText error>
                  {errorMessage}
                </FormHelperText>
              )}
            </Box>

            <Box sx={styles.listItem}>
              <Box sx={styles.labelRow}>
                <Typography id="create-publication-description-label" variant="body2" sx={styles.formLabel}>
                  {messages.description}
                </Typography>
                <Typography variant="caption" sx={styles.characterCounter}>
                  {publicationDescription.length}/{MAX_DESCRIPTION_LENGTH}
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                sx={styles.descriptionTextField}
                placeholder={messages.descriptionPlaceholer}
                value={publicationDescription}
                onChange={(e) => setPublicationDescription(e.target.value)}
                inputProps={{ 'data-test': 'publication-description-input', maxLength: MAX_DESCRIPTION_LENGTH, className: 'no-format', 'aria-labelledby': 'create-publication-description-label' }}
              />
            </Box>

            <Divider sx={styles.divider} />

            <Typography variant="body2" sx={styles.sectionTitle}>
              {messages.selectFolderTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {messages.selectFolderDesc}
            </Typography>

            {renderFolderDropdown()}
          </Box>
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button variant="outlined" color="secondary" size="small" onClick={onClose} data-test="cancel-create-publication-button">
            {messages.cancel}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            disabled={
              trimmedPublicationTitle.length < MIN_TITLE_LENGTH ||
              trimmedPublicationTitle.length > MAX_TITLE_LENGTH ||
              trimmedPublicationTitle.length > MAX_DESCRIPTION_LENGTH ||
              hasSameName
            }
            onClick={() => {
              !folderId ? handleConfirmCreatePublication() : setShowDialog(true);
            }}
            data-test="create-publication-button"
            startIcon={isloading ? <CircularProgress size={16} color="inherit" aria-label="Loading" /> : undefined}
          >
            {messages.create}
          </Button>
        </DialogActions>
      </Dialog>

      {showDialog && (
        <Dialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          fullWidth
          maxWidth="xs"
          data-test="confirm-create-publication"
          aria-labelledby="confirm-create-publication-title"
        >
          <DialogTitle id="confirm-create-publication-title" sx={styles.dialogTitle}>
            {messages.confirmPublication}
            <IconButton
              aria-label="close"
              onClick={() => { setShowDialog(false); onClose(); }}
              data-test="dialog-confirm-create-publication-close-button"
              size="small"
              sx={styles.closeButton}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>{messages.confirmPublicationDesc}</DialogContentText>
          </DialogContent>
          <DialogActions sx={styles.confirmDialogActions}>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              data-test="cancel-create-publication-dialog-button"
              onClick={() => {
                setShowDialog(false);
                onClose();
              }}
            >
              {messages.cancel}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              data-test="confirm-create-publication-dialog-button"
              disabled={isloading}
              startIcon={isloading ? <CircularProgress size={16} color="inherit" aria-label="Loading" /> : undefined}
              onClick={() => {
                handleConfirmCreatePublication();
              }}
            >
              {messages.confirm}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
